const SAUDI_PHONE = /^0\d{9}$/;

const isBlank = (value: any) =>
    value === undefined || value === null || String(value).trim() === '';

export const normalizeSaudiPhone = (value: any): string => {
    const raw = String(value ?? '').trim();
    const digits = raw.replace(/\D/g, '');
    if (!digits) return raw;
    if (digits.startsWith('966') && digits.length === 12) return `0${digits.slice(3)}`;
    if (raw.startsWith('0') && digits.startsWith('0') && digits.length === 10) return digits;
    return digits;
};

export enum HousingIqamaServiceType {
    Housing = 24,
    Iqama = 25,
}

export const HOUSING_IQAMA_SERVICE_TYPE_LABELS: Record<HousingIqamaServiceType, string> = {
    [HousingIqamaServiceType.Housing]: 'Housing',
    [HousingIqamaServiceType.Iqama]: 'Iqama',
};

const SERVICE_TYPE_BY_NAME: Record<string, HousingIqamaServiceType> = {
    housing: HousingIqamaServiceType.Housing,
    iqama: HousingIqamaServiceType.Iqama,
};

export const parseHousingIqamaServiceType = (value: any): HousingIqamaServiceType | undefined => {
    if (isBlank(value)) return undefined;
    const fromName = SERVICE_TYPE_BY_NAME[String(value).trim().toLowerCase()];
    if (fromName !== undefined) return fromName;
    const fromNumber = Number(value);
    if (fromNumber === HousingIqamaServiceType.Housing || fromNumber === HousingIqamaServiceType.Iqama) {
        return fromNumber;
    }
    return undefined;
};

const isPositiveNumber = (value: any) => {
    const amount = Number(value);
    return Number.isFinite(amount) && amount > 0;
};

const isPositiveInteger = (value: any) => {
    const count = Number(value);
    return Number.isInteger(count) && count > 0;
};

export interface HousingIqamaRow {
    phone?: any;
    serviceID?: any;
    amount?: any;
    numberOfInstallments?: any;
    [key: string]: any;
}

export interface InvalidHousingIqamaRow extends HousingIqamaRow {
    rowNumber: number;
    errors: string[];
}

const DATA_FIELDS: (keyof HousingIqamaRow)[] = ['phone', 'serviceID', 'amount', 'numberOfInstallments'];

export const isEmptyHousingIqamaRow = (row: HousingIqamaRow) =>
    DATA_FIELDS.every((field) => isBlank(row?.[field]));

export const toHousingIqamaPayload = (rows: HousingIqamaRow[]) =>
    rows.map((row) => ({
        phone: row.phone,
        serviceID: row.serviceID,
        numberOfInstallments: row.numberOfInstallments,
        amount: row.amount,
    }));

export const validateHousingIqamaRows = (rows: HousingIqamaRow[]) => {
    const invalidRows: InvalidHousingIqamaRow[] = [];
    const normalizedRows: HousingIqamaRow[] = [];

    rows.forEach((row, index) => {
        if (isEmptyHousingIqamaRow(row)) return;

        const errors: string[] = [];
        const phone = normalizeSaudiPhone(row.phone);

        if (isBlank(row.phone)) errors.push('phone is missing');
        else if (!SAUDI_PHONE.test(phone)) errors.push('phone is invalid');

        if (isBlank(row.serviceID)) errors.push('serviceID is missing');
        else if (parseHousingIqamaServiceType(row.serviceID) === undefined) {
            errors.push(`serviceID is invalid. Use ${HousingIqamaServiceType.Housing} (${HOUSING_IQAMA_SERVICE_TYPE_LABELS[HousingIqamaServiceType.Housing]}) or ${HousingIqamaServiceType.Iqama} (${HOUSING_IQAMA_SERVICE_TYPE_LABELS[HousingIqamaServiceType.Iqama]})`);
        }

        if (isBlank(row.amount)) errors.push('amount is missing');
        else if (!isPositiveNumber(row.amount)) errors.push('amount is invalid. Use a positive number');

        if (isBlank(row.numberOfInstallments)) errors.push('numberOfInstallments is missing');
        else if (!isPositiveInteger(row.numberOfInstallments)) errors.push('numberOfInstallments is invalid. Use a whole number greater than 0');

        const serviceID = parseHousingIqamaServiceType(row.serviceID);
        const normalized: HousingIqamaRow = {
            ...row,
            phone: isBlank(row.phone) ? row.phone : phone,
            serviceID: serviceID ?? row.serviceID,
            amount: isBlank(row.amount) ? row.amount : Number(row.amount),
            numberOfInstallments: isBlank(row.numberOfInstallments) ? row.numberOfInstallments : Number(row.numberOfInstallments),
        };

        if (errors.length > 0) {
            invalidRows.push({ ...normalized, rowNumber: index + 2, errors });
        }
        normalizedRows.push(normalized);
    });

    return {
        isValid: invalidRows.length === 0,
        normalizedRows,
        invalidRows,
    };
};

export const prepareHousingIqamaSheet = (rows: HousingIqamaRow[], requiredHeaders: string[]) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return { error: 'Empty File', normalizedRows: [] as HousingIqamaRow[], invalidRows: [] as InvalidHousingIqamaRow[] };
    }

    const dataRows = rows.filter((row) => !isEmptyHousingIqamaRow(row));
    if (dataRows.length === 0) {
        return { error: 'Empty File', normalizedRows: [] as HousingIqamaRow[], invalidRows: [] as InvalidHousingIqamaRow[] };
    }

    const sheetHeaders = new Set<string>();
    dataRows.forEach((row) => Object.keys(row || {}).forEach((key) => sheetHeaders.add(key)));
    const missingHeaders = requiredHeaders.filter((header) => !sheetHeaders.has(header));
    if (missingHeaders.length > 0) {
        return {
            error: `${missingHeaders.join(', ')} ${missingHeaders.length > 1 ? 'columns are' : 'column is'} missing from the sheet`,
            normalizedRows: [] as HousingIqamaRow[],
            invalidRows: [] as InvalidHousingIqamaRow[],
        };
    }

    return { error: '', ...validateHousingIqamaRows(rows) };
};
